import { Box, Typography, useTheme, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState } from "react";
import moment from "moment";

import ReceiptIcon       from "@mui/icons-material/Receipt";
import AddIcon           from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon  from "@mui/icons-material/EditOutlined";
import VisibilityIcon    from "@mui/icons-material/Visibility";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import ListFilterBar from "components/list/ListFilterBar";
import ListDateFilterBar from "components/list/ListDateFilterBar";
import ListSearchField from "components/list/ListSearchField";
import FixedSizeDialog    from "components/fixedSizeDialog";
import useListDateFilter from "hooks/useListDateFilter";
import { currencyFormatter } from "utilities/currencyFormatter";
import { requester }      from "api/pesabackend";
import { useGetInvoicesList } from "api/hooks/billing";

const StatusPill = ({ status }) => {
  const config = status === true
    ? { label: "Paid",      bg: "rgba(29,158,117,0.12)",  color: "#1D9E75" }
    : status === false
    ? { label: "Unpaid",    bg: "rgba(226,75,74,0.1)",    color: "#e24b4a" }
    : { label: "Cancelled", bg: "rgba(136,135,128,0.12)", color: "#888780" };

  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center",
      px: "8px", py: "3px", borderRadius: "20px",
      backgroundColor: config.bg,
    }}>
      <Box sx={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: config.color, mr: "5px", flexShrink: 0 }} />
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: config.color, lineHeight: 1 }}>
        {config.label}
      </Typography>
    </Box>
  );
};

const ActionBtn = ({ label, icon, onClick, color = "default" }) => {
  const colors = {
    default: { color: "#888780",  border: "rgba(136,135,128,0.3)",  hover: "rgba(136,135,128,0.08)"  },
    warning: { color: "#BA7517",  border: "rgba(186,117,23,0.3)",   hover: "rgba(186,117,23,0.08)"   },
    danger:  { color: "#e24b4a",  border: "rgba(226,75,74,0.3)",    hover: "rgba(226,75,74,0.08)"    },
    primary: { color: "#1D9E75",  border: "rgba(29,158,117,0.3)",   hover: "rgba(29,158,117,0.08)"   },
  };
  const c = colors[color] ?? colors.default;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "inline-flex", alignItems: "center", gap: "4px",
        px: "8px", py: "4px", borderRadius: "6px",
        border: `1px solid ${c.border}`, cursor: "pointer",
        color: c.color, fontSize: "12px", fontWeight: 500,
        transition: "all 0.15s",
        "&:hover": { backgroundColor: c.hover },
      }}
    >
      {icon && <Box sx={{ fontSize: "14px", display: "flex" }}>{icon}</Box>}
      {label}
    </Box>
  );
};

export default function Invoices({ setIsSuccess }) {
  const navigate = useNavigate();
  const theme    = useTheme();

  const bodyBg  = theme.palette.background.alt;
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const permissions        = useSelector((s) => s.authReducer.permissions);
  const {
    invoicesList, count, isLoadingInvoicesList, isLoadingMore, hasMore, loadMore,
    dateFilter, setDateFilter, customRange, setCustomRange, applyCustomRange, setNewItemData,
  } = useGetInvoicesList(1, business_branch_id);

  const [openDelete,   setOpenDelete]   = useState(false);
  const [clickedItem,  setClickedItem]  = useState("");
  const [searchContent, setSearchContent] = useState("");

  const { displayDateFilter, handleFilterChange, handleCustomApply } = useListDateFilter({
    dateFilter, setDateFilter, applyCustomRange, customRange,
  });

  const handleClickEditOpen = async (id) => {
    const response = await requester.get(`/billing/invoices/edit_details?id=${id}`);
    if (response.status === 200) navigate("/invoiceEditForm", { state: response.data.data });
  };

  const handleClickDeleteOpen = async (id) => {
    setClickedItem(id);
    const response = await requester.get(`/billing/invoices/edit_details?id=${id}`);
    if (response.status === 200) setOpenDelete(true);
  };

  const handleClickDelete = async () => {
    await requester.post(`/billing/invoices/delete`, { id: clickedItem });
    setIsSuccess(true);
    setNewItemData(true);
    setOpenDelete(false);
  };

  const filtered = invoicesList.filter((r) =>
    r.invoice_number?.toLowerCase().includes(searchContent.toLowerCase())
  );

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "0.5rem 0.5rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <ReceiptIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Bills
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {count} total records
            </Typography>
          </Box>
        </Box>

        {permissions.BCI === 1 && (
          <Button
            onClick={() => navigate("/invoiceForm", { formType: "create" })}
            startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
            sx={{
              fontSize: "13px", fontWeight: 500,
              px: "1rem", py: "0.45rem", borderRadius: "8px",
              backgroundColor: accent, color: "#fff", textTransform: "none",
              "&:hover": { backgroundColor: theme.palette.secondary[500] ?? "#0F6E56" },
            }}
          >
            Create Bill
          </Button>
        )}
      </Box>

      <ListFilterBar>
        <ListDateFilterBar
          embedded
          dateFilter={displayDateFilter}
          customRange={customRange}
          onFilterChange={handleFilterChange}
          onCustomRangeChange={setCustomRange}
          onCustomApply={handleCustomApply}
        />
        <ListSearchField placeholder="Search by bill number…" onSearch={setSearchContent} />
      </ListFilterBar>

      {/* ── Table ── */}
      <ReportTable
        columns={[
          { label: "#",           width: 40 },
          { label: "Bill No." },
          { label: "Date Created" },
          { label: "Amount",      align: "right" },
          { label: "Customer" },
          { label: "Status" },
          { label: "Created By" },
          { label: "Actions",    align: "center" },
        ]}
        rows={filtered}
        loading={isLoadingInvoicesList}
        hasMore={hasMore}
        onLoadMore={loadMore}
        loadingMore={isLoadingMore}
        emptyMessage="No bills found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell>{row.invoice_number}</RCell>
            <RCell>{moment(row.date_created).format("LLL")}</RCell>
            <RCell align="right">{currencyFormatter(parseFloat(row.total_amount))}</RCell>
            <RCell>
              {row.related_customer
                ? `${row.related_customer.related_user.first_name} ${row.related_customer.related_user.last_name}`
                : "—"}
            </RCell>
            <RCell><StatusPill status={row.status} /></RCell>
            <RCell>{`${row.created_by.first_name} ${row.created_by.last_name}`}</RCell>
            <RCell align="center">
              <Box sx={{ display: "flex", gap: "4px", justifyContent: "center", flexWrap: "wrap" }}>
                <ActionBtn label="View"   icon={<VisibilityIcon fontSize="inherit" />}  color="primary" onClick={() => navigate("/invoiceDetails", { state: { id: row.id } })} />
                <ActionBtn label="Edit"   icon={<EditOutlinedIcon fontSize="inherit" />} color="warning" onClick={() => handleClickEditOpen(row.id)} />
                <ActionBtn label="Delete" icon={<DeleteOutlineIcon fontSize="inherit" />} color="danger"  onClick={() => handleClickDeleteOpen(row.id)} />
              </Box>
            </RCell>
          </RRow>
        )}
      />

      {/* ── Delete dialog ── */}
      <FixedSizeDialog
        dialogHeading="Delete this bill?"
        open={openDelete}
        handleClose={() => { setClickedItem(""); setOpenDelete(false); }}
        negativeResponse="Cancel"
        content={
          <Box p="1rem" borderRadius="0.5rem">
            <Typography sx={{ fontSize: "13px", color: textSec, mb: "1rem" }}>
              This action cannot be undone.
            </Typography>
            <Button
              fullWidth variant="contained"
              onClick={handleClickDelete}
              sx={{
                backgroundColor: "#e24b4a", color: "#fff",
                textTransform: "none", borderRadius: "8px",
                "&:hover": { backgroundColor: "#c23a39" },
              }}
            >
              Yes, delete bill
            </Button>
          </Box>
        }
      />
    </Box>
  );
}